import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { has, isEqual } from 'lodash';
import { useParams, useLocation } from 'react-router-dom';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import Dialog from 'components/ui/Dialog';
import Form from 'components/ui/Form';
import { TextField } from '@material-ui/core';
import { FormSectionTitle, GridContainer } from 'styles/form';
import {
  showBillTo,
  updateBillTo,
  createBillTo,
  deleteBillTo,
} from 'api/bill-to';
import AddressFormFields from 'components/general/AddressFormFields';
import PhoneNumber from 'components/ui/PhoneNumber';
import { useSnackBar } from 'context/SnackBarContext';
import { revertPhoneMask } from 'helpers/revert-phone-mask';
import { initialValues, validationSchema } from '../bill-to.config';

const BillToModal = ({
  modalOpen,
  handleModalClose,
  blockRouteChange,
  setBlockRouteChange,
}) => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const routeDepth = pathname.split('/').length - 1;

  const [billTo, setBillTo] = useState();
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  useEffect(async () => {
    if (id !== 'add' && routeDepth <= 2) {
      const { data } = await showBillTo(id);
      setBillTo(data);
    }
  }, []);

  const handleSave = async (values) => {
    if (billTo) {
      try {
        await updateBillTo(id, values);
        setMessage('Saved changes');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to save changes.');
        setSeverity('error');
      }
    } else {
      try {
        await createBillTo(values);
        setMessage('Bill to created');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to create bill to.');
        setSeverity('error');
      }
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  const handleDelete = async () => {
    try {
      await deleteBillTo(id);
      setMessage('Bill to deleted');
      setSeverity('success');
    } catch (error) {
      setMessage('Something went wrong. Failed to delete bill to.');
      setSeverity('error');
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Formik
        initialValues={initialValues(billTo)}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({
          values,
          errors,
          setFieldValue,
          handleChange,
          submitForm,
          touched,
        }) => {
          const {
            name,
            address,
            city,
            state,
            postalCode,
            country,
            contactFirstName,
            contactLastName,
            contactTitle,
            contactEmail,
            contactPhone,
          } = values;

          const initValues = initialValues(billTo);

          const unsavedChange = !isEqual(
            {
              ...values,
              contactPhone: revertPhoneMask(contactPhone),
            },
            initValues,
          );

          if (unsavedChange) {
            setBlockRouteChange(true);
          } else {
            setBlockRouteChange(false);
          }

          return (
            <Dialog
              open={modalOpen}
              handleClose={handleModalClose}
              submitForm={submitForm}
              handleDelete={handleDelete}
              typeText="Bill To"
              blockRouteChange={blockRouteChange}
            >
              <FormSectionTitle>Bill To</FormSectionTitle>
              <Form fullWidth>
                <GridContainer>
                  <TextField
                    color="primary"
                    error={touched.name && has(errors, 'name')}
                    helperText={touched.name && errors.name}
                    label="Bill To"
                    name="name"
                    onChange={handleChange}
                    value={name}
                    variant="outlined"
                    required
                  />

                  <AddressFormFields
                    address={address}
                    country={country}
                    city={city}
                    state={state}
                    postalCode={postalCode}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    fieldsRequired
                  />

                  <TextField
                    color="primary"
                    error={
                      touched.contactFirstName &&
                      has(errors, 'contactFirstName')
                    }
                    fullWidth
                    helperText={
                      touched.contactFirstName && errors.contactFirstName
                    }
                    label="Contact First Name"
                    name="contactFirstName"
                    onChange={handleChange}
                    value={contactFirstName}
                    variant="outlined"
                  />

                  <TextField
                    color="primary"
                    error={
                      touched.contactLastName && has(errors, 'contactLastName')
                    }
                    fullWidth
                    helperText={
                      touched.contactLastName && errors.contactLastName
                    }
                    label="Contact Last Name"
                    name="contactLastName"
                    onChange={handleChange}
                    value={contactLastName}
                    variant="outlined"
                  />

                  <TextField
                    color="primary"
                    error={touched.contactTitle && has(errors, 'contactTitle')}
                    helperText={touched.contactTitle && errors.contactTitle}
                    label="Contact Title"
                    name="contactTitle"
                    onChange={handleChange}
                    value={contactTitle}
                    type="phone"
                    variant="outlined"
                  />

                  <TextField
                    color="primary"
                    error={touched.contactEmail && has(errors, 'contactEmail')}
                    helperText={touched.contactEmail && errors.contactEmail}
                    label="Contact Email"
                    name="contactEmail"
                    onChange={handleChange}
                    value={contactEmail}
                    variant="outlined"
                  />

                  <PhoneNumber
                    label="Contact Phone Number"
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    value={contactPhone}
                    name="contactPhone"
                  />
                </GridContainer>
              </Form>
            </Dialog>
          );
        }}
      </Formik>
    </MuiPickersUtilsProvider>
  );
};

export default BillToModal;
